export type SocialParameterName =
  | 'Communication'
  | 'Emotional Intelligence'
  | 'Trust & Reliability'
  | 'Boundaries'
  | 'Social Confidence'
  | 'Conflict Navigation'
  | 'Relationship Depth';

export interface SocialParameter {
  name: SocialParameterName;
  description: string;
  icon: string;
  color: string;
  improvementTips: string[];
}

export const SOCIAL_PARAMETERS: Record<SocialParameterName, SocialParameter> = {
  'Communication': {
    name: 'Communication',
    description: 'Express thoughts clearly and listen effectively. The foundation of all meaningful connections.',
    icon: 'MessageCircle',
    color: '#3B82F6',
    improvementTips: [
      'Practice active listening without interrupting',
      'Ask open-ended questions to deepen conversations',
      'Share your thoughts openly and honestly',
      'Pay attention to non-verbal cues',
      'Summarize what others say to confirm understanding'
    ]
  },
  'Emotional Intelligence': {
    name: 'Emotional Intelligence',
    description: 'Recognize, understand, regulate, and respond appropriately to emotions in yourself and others.',
    icon: 'Heart',
    color: '#EC4899',
    improvementTips: [
      'Practice naming your emotions throughout the day',
      'Pause before reacting to strong emotions',
      'Try to understand others\' perspectives',
      'Notice how different situations affect your mood',
      'Validate others\' feelings before offering solutions'
    ]
  },
  'Trust & Reliability': {
    name: 'Trust & Reliability',
    description: 'Be consistently dependable, honest, and safe. Build a reputation as someone others can count on.',
    icon: 'Shield',
    color: '#10B981',
    improvementTips: [
      'Follow through on commitments, no matter how small',
      'Be honest even when it\'s uncomfortable',
      'Arrive on time and prepared',
      'Keep confidences and private information private',
      'Admit mistakes and take responsibility'
    ]
  },
  'Boundaries': {
    name: 'Boundaries',
    description: 'Set, communicate, and respect personal limits. Know when to say no and honor others\' limits.',
    icon: 'ShieldCheck',
    color: '#F59E0B',
    improvementTips: [
      'Practice saying no without over-explaining',
      'Identify your non-negotiables in relationships',
      'Communicate limits calmly and clearly',
      'Respect when others set boundaries with you',
      'Check in with yourself about what feels right'
    ]
  },
  'Social Confidence': {
    name: 'Social Confidence',
    description: 'Feel comfortable and authentic in social situations. Engage with ease across different contexts.',
    icon: 'Sparkles',
    color: '#8B5CF6',
    improvementTips: [
      'Start conversations with small talk practice',
      'Attend social events outside your comfort zone',
      'Focus on being interested rather than interesting',
      'Accept that awkward moments are normal',
      'Celebrate small wins in social situations'
    ]
  },
  'Conflict Navigation': {
    name: 'Conflict Navigation',
    description: 'Handle disagreements constructively. Transform conflicts into opportunities for growth.',
    icon: 'Scale',
    color: '#EF4444',
    improvementTips: [
      'Address issues early before they escalate',
      'Use "I" statements instead of blame',
      'Seek to understand before being understood',
      'Look for win-win solutions',
      'Know when to take a break and revisit'
    ]
  },
  'Relationship Depth': {
    name: 'Relationship Depth',
    description: 'Build meaningful, long-term connections. Invest in relationships that matter and nurture them over time.',
    icon: 'Users',
    color: '#06B6D4',
    improvementTips: [
      'Schedule regular time for important relationships',
      'Remember and follow up on personal details',
      'Share vulnerably to deepen connection',
      'Be present during quality time together',
      'Celebrate others\' successes genuinely'
    ]
  }
};

export const SOCIAL_PARAMETER_NAMES: SocialParameterName[] = [
  'Communication',
  'Emotional Intelligence',
  'Trust & Reliability',
  'Boundaries',
  'Social Confidence',
  'Conflict Navigation',
  'Relationship Depth'
];

export const BASE_XP_PER_POINT = 50;
export const LEVEL_SCALING_PERCENT = 5;
